import { Dispatch, SetStateAction, useMemo } from "react"
import { Box, Card, Typography, Button } from '@mui/material'
import { Warning as WarningIcon } from '@mui/icons-material'
import { ChildCustomerState } from "../../../../redux/childCustomer"
import { CustomerState } from "../../../../redux/customer"
import { DisplayResultsBy, OpenLogResultModal, Room, WaterlineTestResult } from "../types"
import TestResultTable from "./TestResultsTable"

type TestResultsTableContainer = {
  testResults: WaterlineTestResult[],
  displayResultsBy: DisplayResultsBy,
  getTestResults: () => void,
  rooms: Room[],
  getCompliance: () => void,
  customer: CustomerState | ChildCustomerState,
  setTabIndex: Dispatch<SetStateAction<number>>,
  setOpenModal: Dispatch<SetStateAction<OpenLogResultModal>>,
}

const TestResultsTableContainer = (props: TestResultsTableContainer) => {
  const {
    testResults,
    displayResultsBy,
    getTestResults,
    rooms,
    getCompliance,
    customer,
    setTabIndex,
    setOpenModal,
  } = props

  const hasUnmatchedResults = displayResultsBy === 'roomChair' && !testResults[0]?.room

  const unmatchedResults = useMemo(() => {
    if (hasUnmatchedResults) {
      const results: WaterlineTestResult[] = testResults.map(result => ({ ...result, location: result.location ?? 'No Room/Chair' }))

      const resultsByLocation = new Map<string, WaterlineTestResult[]>()

      results.forEach(result => {
        resultsByLocation.get(result.location)?.push(result)
          ?? resultsByLocation.set(result.location, [result])
      })

      return resultsByLocation
    }
  }, [hasUnmatchedResults])

  const sortedUnmatchedKeys = useMemo(() => {
    if (unmatchedResults) {
      const sorted = Array.from(unmatchedResults.keys())
        .sort((a, b) => {
          return a.localeCompare(b)
        })

      return sorted
    }
  }, [unmatchedResults])

  return (
    <>
      {
        unmatchedResults && sortedUnmatchedKeys ?
          <Box className='m-b-16'>
            <Card variant="outlined" style={{ marginBottom: '15px' }}>
              <Box className="box-header flex flex-align-center flex-space-between">
                <Box className="flex flex-align-center">
                  <WarningIcon sx={{ color: '#f4c972' }} />
                  <Box>Unmatched Results</Box>
                </Box>
              </Box>
              <Box sx={{ backgroundColor: '#f2dede', padding: '10px' }}>
                <Typography sx={{ color: '#c86148', fontSize: '13px' }}>These results did not match any of the chairs set up for your practice.
                  Please assign each result to a chair. To select a room/chair combination,
                  please add a room/chair under <Button onClick={(event) => setTabIndex(2)} variant="text" sx={{ color: '#69c4e8', fontSize: '13px', padding: 0 }}>Manage Room/Chair section.</Button>
                </Typography>
              </Box>
              {
                sortedUnmatchedKeys
                  .map(unmatchedKey => (
                    <TestResultTable
                      key={unmatchedKey}
                      testResults={unmatchedResults.get(unmatchedKey) ?? []}
                      displayResultsBy={displayResultsBy}
                      getTestResults={getTestResults}
                      rooms={rooms}
                      getCompliance={getCompliance}
                      customer={customer}
                      hasUnmatchedResults={hasUnmatchedResults}
                      setOpenModal={setOpenModal}
                    />
                  ))
              }
            </Card>
          </Box>
          :
          <TestResultTable
            testResults={testResults}
            displayResultsBy={displayResultsBy}
            getTestResults={getTestResults}
            rooms={rooms}
            getCompliance={getCompliance}
            customer={customer}
            setOpenModal={setOpenModal}
          />
      }
    </>
  )
}

export default TestResultsTableContainer
